import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Users } from 'lucide-react';

interface Patient {
  id?: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  geschlecht: string;
  zimmernummer: string;
  aufnahmedatum: string;
  diagnose: string;
  besonderheiten: string;
}

interface PatientManagementProps {
  patients: Patient[];
  onLoadPatients: () => void;
  onSavePatient: (patient: Patient) => Promise<any>;
  onSelectPatient: (patient: Patient | null) => void;
  selectedPatient: Patient | null;
}

const PatientManagement = ({ 
  patients, 
  onLoadPatients, 
  onSavePatient, 
  onSelectPatient, 
  selectedPatient 
}: PatientManagementProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Patient>({
    vorname: '',
    nachname: '',
    geburtsdatum: '',
    geschlecht: '',
    zimmernummer: '',
    aufnahmedatum: '',
    diagnose: '',
    besonderheiten: ''
  });

  useEffect(() => {
    onLoadPatients();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.vorname || !formData.nachname) {
      return;
    }

    const result = await onSavePatient(formData);
    if (result) {
      setFormData({
        vorname: '',
        nachname: '',
        geburtsdatum: '',
        geschlecht: '',
        zimmernummer: '',
        aufnahmedatum: '',
        diagnose: '',
        besonderheiten: ''
      });
      setIsDialogOpen(false);
    }
  };

  const handleInputChange = (field: keyof Patient, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Patientenverwaltung</h2>
          <p className="text-gray-600">Verwalten Sie Patientendaten und wählen Sie einen Patienten für die Pflegeplanung aus.</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Neuer Patient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Neuen Patienten anlegen</DialogTitle>
              <DialogDescription>
                Geben Sie die Patientendaten ein. Pflichtfelder sind mit * markiert.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vorname">Vorname *</Label>
                  <Input
                    id="vorname"
                    value={formData.vorname}
                    onChange={(e) => handleInputChange('vorname', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nachname">Nachname *</Label>
                  <Input
                    id="nachname"
                    value={formData.nachname}
                    onChange={(e) => handleInputChange('nachname', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="geburtsdatum">Geburtsdatum</Label>
                  <Input
                    id="geburtsdatum"
                    type="date"
                    value={formData.geburtsdatum}
                    onChange={(e) => handleInputChange('geburtsdatum', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="geschlecht">Geschlecht</Label>
                  <Select value={formData.geschlecht} onValueChange={(value) => handleInputChange('geschlecht', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Geschlecht wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="männlich">Männlich</SelectItem>
                      <SelectItem value="weiblich">Weiblich</SelectItem>
                      <SelectItem value="divers">Divers</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zimmernummer">Zimmernummer</Label>
                  <Input
                    id="zimmernummer"
                    value={formData.zimmernummer}
                    onChange={(e) => handleInputChange('zimmernummer', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="aufnahmedatum">Aufnahmedatum</Label>
                  <Input
                    id="aufnahmedatum"
                    type="date"
                    value={formData.aufnahmedatum}
                    onChange={(e) => handleInputChange('aufnahmedatum', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="diagnose">Diagnose</Label>
                <Textarea
                  id="diagnose"
                  value={formData.diagnose}
                  onChange={(e) => handleInputChange('diagnose', e.target.value)}
                  placeholder="Hauptdiagnose und relevante Nebendiagnosen"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="besonderheiten">Besonderheiten</Label>
                <Textarea
                  id="besonderheiten"
                  value={formData.besonderheiten}
                  onChange={(e) => handleInputChange('besonderheiten', e.target.value)}
                  placeholder="Allergien, Besonderheiten, wichtige Hinweise"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">
                  Patient speichern
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {selectedPatient && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-900">Ausgewählter Patient</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><strong>Name:</strong> {selectedPatient.vorname} {selectedPatient.nachname}</div>
              <div><strong>Zimmer:</strong> {selectedPatient.zimmernummer}</div>
              <div><strong>Geburtsdatum:</strong> {selectedPatient.geburtsdatum}</div>
              <div><strong>Geschlecht:</strong> {selectedPatient.geschlecht}</div>
              <div className="col-span-2"><strong>Diagnose:</strong> {selectedPatient.diagnose}</div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4"
              onClick={() => onSelectPatient(null)}
            >
              Auswahl aufheben
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Patientenliste ({patients.length})</h3>
        </div>
        
        {patients.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Noch keine Patienten angelegt.</p>
              <p className="text-sm text-gray-500">Klicken Sie auf "Neuer Patient", um zu beginnen.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {patients.map((patient) => (
              <Card 
                key={patient.id} 
                className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                  selectedPatient?.id === patient.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                }`}
                onClick={() => onSelectPatient(patient)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="grid grid-cols-2 gap-4 flex-1">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {patient.vorname} {patient.nachname}
                        </h4>
                        <p className="text-sm text-gray-600">Zimmer: {patient.zimmernummer}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Geboren: {patient.geburtsdatum}
                        </p>
                        <p className="text-sm text-gray-600">
                          Aufnahme: {patient.aufnahmedatum}
                        </p>
                      </div>
                      {patient.diagnose && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-700">
                            <strong>Diagnose:</strong> {patient.diagnose}
                          </p>
                        </div>
                      )}
                    </div>
                    {selectedPatient?.id === patient.id && (
                      <div className="ml-4">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientManagement;